import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Sidebar from '../Sidebar'
import ArticleTypeModal from '../ArticleTypeModal'
import { useView } from '../../contexts/ViewContext'

interface AdminLayoutProps {
  children: ReactNode
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [isArticleModalOpen, setArticleModalOpen] = useState(false)
  const { view, setView } = useView()
  const location = useLocation()

  const getPageTitle = () => {
    const titleMap: Record<string, string> = {
      '/admin/dashboard': 'Hi, Good Morning!',
      '/admin/content': 'Content',
      '/admin/subscribers': 'Subscribers',
      '/admin/admin-users': 'Admin Users',
      '/admin/press-release': 'Press Release',
      '/admin/strategic-pillars': 'Strategic Pillars',
      '/admin/programmes': 'Programmes',
      '/admin/publications': 'Publications',
      '/admin/photo-gallery': 'Photo Gallery',
      '/admin/videos': 'Videos',
      '/admin/about': 'About',
      '/admin/opportunities': 'Opportunities',
      '/admin/events': 'Events',
      '/admin/announcement': 'Announcement',
    }
    return titleMap[location.pathname] || 'Admin Panel'
  }

  const getHeaderDescription = () => {
    if (view.subPage === 'detail' || view.subPage === 'edit' || view.subPage === 'new' || view.subPage === 'folder') return ''
    switch (location.pathname) {
      case '/admin/dashboard':
        return 'Manage your website content'
      case '/admin/content':
        return 'Manage your website content'
      default:
        return ''
    }
  }

  const handleCreateNew = (articleType: 'Press Release' | 'Strategic Pillars' | 'Programmes') => {
    setArticleModalOpen(false)
    setView({ page: articleType, subPage: 'new' })
  }

  const isEditorMode = view.subPage === 'edit' || view.subPage === 'new'

  if (isEditorMode) {
    return (
      <div className="h-screen bg-white font-sans">
        <div className="h-full overflow-y-auto">
          {children}
        </div>
        <ArticleTypeModal
          isOpen={isArticleModalOpen}
          onClose={() => setArticleModalOpen(false)}
          onSelect={handleCreateNew}
        />
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-white font-sans">
      <Sidebar
        openArticleModal={() => setArticleModalOpen(true)}
      />
      <main className="flex-1 flex flex-col overflow-y-auto">
        <header className="bg-white border border-gray-200 px-8 py-4 flex justify-between items-center m-8 rounded-lg">
          <div>
            <h1 className="text-xl font-medium text-gray-800">{getPageTitle()}</h1>
            {getHeaderDescription() && <p className="text-sm text-gray-500">{getHeaderDescription()}</p>}
          </div>
          <Link
            to="https://afsa-website.netlify.app/"
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 bg-white  rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
            target="_blank"
          >
            View Website
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.6062 9.035V16.964C16.6084 17.266 16.5502 17.5654 16.4352 17.8446C16.3202 18.1238 16.1507 18.3773 15.9365 18.5902C15.7223 18.8031 15.4678 18.9711 15.1878 19.0844C14.9079 19.1977 14.6082 19.254 14.3062 19.25H3.0492C2.74613 19.2539 2.44534 19.1971 2.16459 19.0828C1.88383 18.9686 1.6288 18.7993 1.41452 18.585C1.20024 18.3706 1.03107 18.1155 0.916971 17.8347C0.802875 17.5539 0.74617 17.2531 0.750201 16.95V5.693C0.746579 5.38996 0.803597 5.08925 0.917901 4.80856C1.0322 4.52788 1.20148 4.27288 1.41578 4.05858C1.63009 3.84428 1.88508 3.675 2.16576 3.5607C2.44645 3.4464 2.74716 3.38938 3.0502 3.393H10.9782M19.2492 0.75L8.6782 11.321M19.2492 0.75H13.9632M19.2492 0.75V6.036" stroke="#00764B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </header>
        <div className="flex-1 p-11 sm:p-6 lg:py-8 lg:px-20">
          {children}
        </div>
      </main>
      <ArticleTypeModal
        isOpen={isArticleModalOpen}
        onClose={() => setArticleModalOpen(false)}
        onSelect={handleCreateNew}
      />
    </div>
  )
}

export default AdminLayout